import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { UncontrolledTreeEnvironment, Tree, StaticTreeDataProvider } from 'react-complex-tree';
import 'react-complex-tree/lib/style-modern.css';
import './FileExplorer.css';
import { api } from '../api';

/**
 * FileExplorer Component
 *
 * A collapsible file/folder explorer sidebar similar to VS Code's file explorer.
 * Fetches real data from the backend and displays:
 * - User's own files and tables
 * - Tables shared by the user (outgoing shares)
 * - Tables shared with the user (incoming shares)
 */
const FileExplorer = forwardRef(({ isOpen, onToggle, onFileSelect, onRefresh }, ref) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState(['root']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render on refresh

  // Backend data
  const [filesWithTables, setFilesWithTables] = useState([]);
  const [sharedTables, setSharedTables] = useState([]);
  const [outgoingShares, setOutgoingShares] = useState([]);

  // Fetch data from backend
  const fetchData = async () => {
    console.log('[FileExplorer] fetchData called');
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [filesRes, sharedRes, outgoingRes] = await Promise.all([
        api.get('/api/files/with-tables'),
        api.get('/api/shared-tables'),
        api.get('/api/shares/outgoing')
      ]);

      // Ensure we have arrays for all data
      const filesList = Array.isArray(filesRes) ? filesRes : [];

      setFilesWithTables(filesList);
      setSharedTables(Array.isArray(sharedRes) ? sharedRes : []);
      setOutgoingShares(Array.isArray(outgoingRes) ? outgoingRes : []);

      // Debug logging
      console.log('[FileExplorer] API responses:', {
        filesWithTables: filesRes,
        sharedTables: sharedRes,
        outgoingShares: outgoingRes
      });
      console.log('[FileExplorer] Processed data:', {
        filesCount: filesList.length,
        totalTables: filesList.reduce((sum, f) => sum + f.tables.length, 0),
        fileDetails: filesList.map(f => ({ filename: f.filename, tableCount: f.tables.length }))
      });
    } catch (err) {
      console.error('Error fetching file explorer data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Expose refresh method to parent component via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchData
  }));

  // Build tree data from backend data
  const treeData = useMemo(() => {
    if (loading) {
      return {
        root: {
          index: 'root',
          canMove: false,
          isFolder: true,
          children: ['loading'],
          data: 'Root',
          canRename: false,
        },
        loading: {
          index: 'loading',
          canMove: false,
          isFolder: false,
          children: [],
          data: 'Loading...',
        }
      };
    }

    const tree = {
      root: {
        index: 'root',
        canMove: false,
        isFolder: true,
        children: ['files', 'shared', 'shared-with-me'],
        data: 'Root',
        canRename: false,
      },
    };

    // ===== My Files Section =====
    console.log('[FileExplorer] Building tree with filesWithTables:', filesWithTables);
    const fileChildren = [];
    filesWithTables.forEach(fileData => {
      const fileId = `file-${fileData.filename}`;
      fileChildren.push(fileId);

      // Get tables for this specific file
      const fileTables = fileData.tables || [];
      console.log(`[FileExplorer] File ${fileData.filename} has ${fileTables.length} tables:`, fileTables.map(t => t.name));

      // Remove .json extension for display
      const displayName = fileData.filename.endsWith('.json')
        ? fileData.filename.slice(0, -5)
        : fileData.filename;

      tree[fileId] = {
        index: fileId,
        canMove: false,
        isFolder: true,
        children: fileTables.map(t => `table-${fileData.filename}-${t.id}`),
        data: displayName,
        canRename: false,
      };

      // Add tables as children
      fileTables.forEach(table => {
        tree[`table-${fileData.filename}-${table.id}`] = {
          index: `table-${fileData.filename}-${table.id}`,
          canMove: false,
          isFolder: false,
          children: [],
          data: table.name || 'Untitled Table',
          tableId: table.id,
          filename: fileData.filename,
          type: 'my-table'
        };
      });
    });

    tree.files = {
      index: 'files',
      canMove: false,
      isFolder: true,
      children: fileChildren,
      data: '📁 My Files',
      canRename: false,
    };

    console.log('[FileExplorer] Tree built with file children:', fileChildren);

    // ===== Shared (Outgoing) Section =====
    // Group by shared_with_user_email, then by permission
    const outgoingByUser = {};
    outgoingShares.forEach(share => {
      const email = share.shared_with_user_email || share.shared_with_email || 'Unknown User';
      // Skip entries with no valid email
      if (!email || email === 'Unknown User') return;

      if (!outgoingByUser[email]) {
        outgoingByUser[email] = { read: [], write: [] };
      }
      const permission = share.permission || 'read';
      outgoingByUser[email][permission].push(share);
    });

    const sharedChildren = [];
    Object.entries(outgoingByUser).forEach(([email, perms]) => {
      const userId = `shared-user-${email}`;
      sharedChildren.push(userId);

      const userChildren = [];

      // Add Read Only folder if there are read shares
      if (perms.read.length > 0) {
        const readOnlyId = `${userId}-readonly`;
        userChildren.push(readOnlyId);
        tree[readOnlyId] = {
          index: readOnlyId,
          canMove: false,
          isFolder: true,
          children: perms.read.map(s => `shared-out-${s.id}`),
          data: 'Read Only',
          canRename: false,
        };

        perms.read.forEach(share => {
          tree[`shared-out-${share.id}`] = {
            index: `shared-out-${share.id}`,
            canMove: false,
            isFolder: false,
            children: [],
            data: share.table_name || 'Unknown Table',
            shareId: share.id,
            tableId: share.table_id,
            type: 'shared-out'
          };
        });
      }

      // Add Read/Write folder if there are write shares
      if (perms.write.length > 0) {
        const readWriteId = `${userId}-readwrite`;
        userChildren.push(readWriteId);
        tree[readWriteId] = {
          index: readWriteId,
          canMove: false,
          isFolder: true,
          children: perms.write.map(s => `shared-out-${s.id}`),
          data: 'Read/Write',
          canRename: false,
        };

        perms.write.forEach(share => {
          tree[`shared-out-${share.id}`] = {
            index: `shared-out-${share.id}`,
            canMove: false,
            isFolder: false,
            children: [],
            data: share.table_name || 'Unknown Table',
            shareId: share.id,
            tableId: share.table_id,
            type: 'shared-out'
          };
        });
      }

      tree[userId] = {
        index: userId,
        canMove: false,
        isFolder: true,
        children: userChildren,
        data: email,
        canRename: false,
      };
    });

    tree.shared = {
      index: 'shared',
      canMove: false,
      isFolder: true,
      children: sharedChildren,
      data: '🔗 Shared With Others',
      canRename: false,
    };

    // ===== Shared With Me (Incoming) Section =====
    // Group by owner_email, then by permission
    const incomingByUser = {};
    sharedTables.forEach(share => {
      const email = share.owner_email || share.owner?.email || 'Unknown Owner';
      // Skip entries with no valid email
      if (!email || email === 'Unknown Owner') return;

      if (!incomingByUser[email]) {
        incomingByUser[email] = { read: [], write: [] };
      }
      const permission = share.permission || 'read';
      incomingByUser[email][permission].push(share);
    });

    const sharedWithMeChildren = [];
    Object.entries(incomingByUser).forEach(([email, perms]) => {
      const userId = `received-user-${email}`;
      sharedWithMeChildren.push(userId);

      const userChildren = [];

      // Add Read Only folder
      if (perms.read.length > 0) {
        const readOnlyId = `${userId}-readonly`;
        userChildren.push(readOnlyId);
        tree[readOnlyId] = {
          index: readOnlyId,
          canMove: false,
          isFolder: true,
          children: perms.read.map(s => `shared-in-${s.table_id}`),
          data: 'Read Only',
          canRename: false,
        };

        perms.read.forEach(share => {
          tree[`shared-in-${share.table_id}`] = {
            index: `shared-in-${share.table_id}`,
            canMove: false,
            isFolder: false,
            children: [],
            data: share.table?.name || 'Unknown Table',
            tableId: share.table_id,
            tableData: share.table,
            type: 'shared-in',
            permission: 'read'
          };
        });
      }

      // Add Read/Write folder
      if (perms.write.length > 0) {
        const readWriteId = `${userId}-readwrite`;
        userChildren.push(readWriteId);
        tree[readWriteId] = {
          index: readWriteId,
          canMove: false,
          isFolder: true,
          children: perms.write.map(s => `shared-in-${s.table_id}`),
          data: 'Read/Write',
          canRename: false,
        };

        perms.write.forEach(share => {
          tree[`shared-in-${share.table_id}`] = {
            index: `shared-in-${share.table_id}`,
            canMove: false,
            isFolder: false,
            children: [],
            data: share.table?.name || 'Unknown Table',
            tableId: share.table_id,
            tableData: share.table,
            type: 'shared-in',
            permission: 'write'
          };
        });
      }

      tree[userId] = {
        index: userId,
        canMove: false,
        isFolder: true,
        children: userChildren,
        data: email,
        canRename: false,
      };
    });

    tree['shared-with-me'] = {
      index: 'shared-with-me',
      canMove: false,
      isFolder: true,
      children: sharedWithMeChildren,
      data: '👥 Shared With Me',
      canRename: false,
    };

    return tree;
  }, [loading, filesWithTables, sharedTables, outgoingShares, refreshKey]);

  const dataProvider = useMemo(
    () => {
      console.log('[FileExplorer] Creating new StaticTreeDataProvider');
      return new StaticTreeDataProvider(treeData, (item, data) => ({ ...item, data }));
    },
    [treeData, refreshKey]
  );

  const handleSelectItems = (items) => {
    setSelectedItems(items);
    if (items.length > 0 && onFileSelect) {
      const selectedItem = treeData[items[0]];

      // Handle file node clicks (folders starting with 'file-')
      if (selectedItem.isFolder && selectedItem.index.startsWith('file-')) {
        const filename = selectedItem.index.replace('file-', '');
        onFileSelect({
          type: 'file',
          filename: filename
        });
      }
      // Handle table node clicks (non-folders)
      else if (!selectedItem.isFolder) {
        onFileSelect({
          id: selectedItem.tableId,
          type: selectedItem.type,
          data: selectedItem.tableData,
          permission: selectedItem.permission,
          filename: selectedItem.filename
        });
      }
    }
  };

  const handleRefresh = async () => {
    console.log('[FileExplorer] handleRefresh called, forcing full remount');
    // Save current expanded items
    const currentExpanded = [...expandedItems];
    // Clear state to unmount tree
    setExpandedItems([]);
    setSelectedItems([]);
    // Increment refresh key BEFORE fetching to force full remount
    setRefreshKey(prev => prev + 1);
    await fetchData();
    // Restore expanded items after a small delay
    setTimeout(() => {
      setExpandedItems(currentExpanded);
    }, 50);
    if (onRefresh) {
      await onRefresh();
    }
  };

  return (
    <div className={`file-explorer ${isOpen ? 'open' : 'closed'}`}>
      <div className="file-explorer-header">
        <button
          className="toggle-button"
          onClick={onToggle}
          title={isOpen ? "Collapse Explorer" : "Expand Explorer"}
        >
          {isOpen ? '◀' : '▶'}
        </button>
        {isOpen && (
          <>
            <span className="explorer-title">EXPLORER</span>
            <div className="explorer-actions">
              <button
                className="action-button"
                title="Refresh"
                onClick={handleRefresh}
              >
                🔄
              </button>
            </div>
          </>
        )}
      </div>

      {isOpen && (
        <div className="file-explorer-content">
          {error && (
            <div style={{ padding: '1rem', color: '#ef4444', fontSize: '0.875rem' }}>
              Error: {error}
            </div>
          )}
          {!loading && expandedItems.length > 0 && (
            <UncontrolledTreeEnvironment
              key={`tree-${refreshKey}`}
              dataProvider={dataProvider}
              getItemTitle={(item) => item.data}
              viewState={{
                'file-tree': {
                  selectedItems,
                  expandedItems,
                },
              }}
              onSelectItems={handleSelectItems}
              onExpandItem={(item) => setExpandedItems([...expandedItems, item.index])}
              onCollapseItem={(item) => setExpandedItems(expandedItems.filter(id => id !== item.index))}
              canDragAndDrop={false}
              canDropOnFolder={false}
              canReorderItems={false}
            >
              <Tree treeId="file-tree" rootItem="root" treeLabel="File Explorer" />
            </UncontrolledTreeEnvironment>
          )}
        </div>
      )}
    </div>
  );
});

export default FileExplorer;
